import { useState } from 'react';
import { Card, Button, LoadingSpinner } from '../components/Layout';
import api from '../services/api';

const RATING: Record<string, string> = { exceeding: 'Exceeding expectations', meeting: 'Meeting expectations', not_yet: 'Not yet meeting expectations' };
const CP: Record<string, string> = { '30_day': '30-day', '60_day': '60-day', '90_day': '90-day', '180_day': '180-day' };
const cpLabel = (k: string) => CP[k] || (k?.startsWith('annual_') ? `Annual (${k.slice(7)})` : k);

const INPUT_QS: { key: string; label: string }[] = [
  { key: 'goingWell', label: 'What is going well?' },
  { key: 'struggling', label: 'What are you struggling with — a workstation, a process, anything?' },
  { key: 'trainingHelp', label: 'What training or help would make the biggest difference?' },
  { key: 'otherRole', label: 'Is there another workstation or role you want to learn?' },
  { key: 'moreResponsibility', label: 'Are you interested in more responsibility or leadership?' },
  { key: 'inTheWay', label: 'Is anything getting in the way of doing your best work?' },
  { key: 'anythingElse', label: 'Anything else?' },
];

// Development reviews for the employee: give input while a review is being written,
// and read + acknowledge once it's completed. Password-gated like Records to review.
export function MyReviewsPage() {
  const [token, setToken] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [unlocking, setUnlocking] = useState(false);
  const [unlockError, setUnlockError] = useState('');
  const [reviews, setReviews] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<any>(null);
  const unlocked = !!token;

  const unlock = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password) return;
    setUnlocking(true); setUnlockError('');
    try {
      const t = await api.reviewsUnlock(password);
      if (!t) { setUnlockError('Incorrect password.'); return; }
      setPassword(''); setToken(t); setLoading(true);
      setReviews(await api.getMyReviews(t)); setLoading(false);
    } catch { setUnlockError('Could not unlock.'); }
    finally { setUnlocking(false); }
  };
  const refresh = async () => { if (token) setReviews(await api.getMyReviews(token)); setSelected(null); };

  if (!unlocked) {
    return (
      <div className="space-y-6 max-w-md mx-auto">
        <Card>
          <h1 className="text-2xl font-bold text-white mb-2">My Reviews</h1>
          <p className="text-sm text-gray-400 mb-4">Your reviews are only shown after you enter your password, in case this screen is shared.</p>
          <form onSubmit={unlock} className="space-y-3">
            {unlockError && <div className="bg-red-900/50 border border-red-500 text-red-200 px-3 py-2 rounded-lg text-sm">{unlockError}</div>}
            <input type="password" autoComplete="current-password" value={password} onChange={e => setPassword(e.target.value)}
              placeholder="Your password" className="w-full px-3 py-2 rounded-lg bg-gray-900 border border-gray-700 text-white" />
            <Button onClick={() => {}} disabled={unlocking}>{unlocking ? 'Checking…' : 'Unlock'}</Button>
          </form>
        </Card>
      </div>
    );
  }

  if (loading) return <div className="flex justify-center p-8"><LoadingSpinner /></div>;
  if (selected) return <ReviewDetail review={selected} token={token!} onBack={refresh} />;

  return (
    <div className="space-y-6">
      <Card>
        <h1 className="text-2xl font-bold text-white mb-4">My Reviews</h1>
        {reviews.length === 0 ? <p className="text-gray-400 py-8 text-center">Nothing right now.</p> : (
          <div className="space-y-3">
            {reviews.map(r => {
              const needsInput = r.status === 'waiting_for_employee' && !r.employeeAnswers?.employeeInputAt;
              const needsAck = r.status === 'completed' && !r.acknowledgedAt;
              return (
                <button key={r.id} onClick={() => setSelected(r)} className="block w-full text-left rounded-lg border border-gray-700 bg-gray-900 p-4 hover:bg-gray-800">
                  <div className="flex items-center justify-between">
                    <span className="text-white font-medium">{cpLabel(r.checkpointKey)} review</span>
                    {needsInput && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-900/50 text-indigo-200 border border-indigo-700">Your input needed</span>}
                    {needsAck && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-900/50 text-amber-200 border border-amber-700">Please acknowledge</span>}
                    {!needsInput && !needsAck && <span className="text-xs text-gray-500">{r.status.replace(/_/g, ' ')}</span>}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}

function ReviewDetail({ review, token, onBack }: { review: any; token: string; onBack: () => void }) {
  const isCompleted = review.status === 'completed';
  const [answers, setAnswers] = useState<any>(review.employeeAnswers || {});
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState('');

  const saveInput = async () => {
    setBusy(true); setError('');
    try { await api.submitReviewInput(review.id, answers, token); setDone('Thanks — your input was sent to your manager.'); }
    catch (e: any) { setError(e.message); } finally { setBusy(false); }
  };
  const acknowledge = async () => {
    setBusy(true); setError('');
    try { await api.acknowledgeReview(review.id, comment, token); setDone('Acknowledged.'); }
    catch (e: any) { setError(e.message); } finally { setBusy(false); }
  };

  if (done) return (
    <div className="max-w-2xl mx-auto"><Card>
      <div className="bg-green-900/30 border border-green-600 rounded-lg p-4 text-green-100">{done}</div>
      <div className="mt-4"><Button onClick={onBack}>Back to my reviews</Button></div>
    </Card></div>
  );

  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      <Card>
        <button onClick={onBack} className="text-sm text-gray-400 hover:text-gray-200 mb-3">← Back</button>
        <h1 className="text-2xl font-bold text-white mb-1">{cpLabel(review.checkpointKey)} review</h1>
        {error && <div className="my-2 bg-red-900/50 border border-red-500 text-red-200 px-3 py-2 rounded-lg text-sm">{error}</div>}

        {isCompleted ? (
          <div className="space-y-4 mt-3">
            <div className="rounded-lg bg-gray-900 border border-gray-700 p-3">
              <div className="text-gray-400 text-xs uppercase tracking-wide">Overall</div>
              <div className="text-white font-semibold">{RATING[review.overallRating] || '—'}</div>
              {review.overallExplanation && <div className="text-gray-200 mt-1 whitespace-pre-wrap">{review.overallExplanation}</div>}
            </div>
            {['strengths', 'areasToImprove', 'trainingNeeds', 'supportProvided', 'goals', 'stationsNow', 'nextStation', 'crossTraining'].map(k => review[k] ? (
              <div key={k} className="rounded-lg bg-gray-900 border border-gray-700 p-3">
                <div className="text-gray-400 text-xs uppercase tracking-wide">{k.replace(/([A-Z])/g, ' $1')}</div>
                <div className="text-gray-200 whitespace-pre-wrap">{review[k]}</div>
              </div>
            ) : null)}
            {!review.acknowledgedAt ? (
              <div className="rounded-lg bg-gray-900 border border-gray-700 p-3 space-y-2">
                <p className="text-sm text-gray-300">Acknowledging means you have read this review. It does not mean you agree with all of it.</p>
                <textarea rows={2} value={comment} onChange={e => setComment(e.target.value)} placeholder="Add a comment (optional)"
                  className="w-full px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-white" />
                <Button onClick={acknowledge} disabled={busy}>{busy ? 'Saving…' : 'Acknowledge'}</Button>
              </div>
            ) : <div className="text-sm text-green-300">You acknowledged this review.</div>}
          </div>
        ) : (
          <div className="space-y-4 mt-3">
            <p className="text-sm text-gray-400">Your manager is preparing your review. Share anything you'd like — all optional. It's shown to them next to their notes.</p>
            {INPUT_QS.map(q => (
              <div key={q.key}>
                <label className="block text-sm font-medium text-gray-200 mb-1">{q.label}</label>
                <textarea rows={2} value={answers[q.key] || ''} onChange={e => setAnswers({ ...answers, [q.key]: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg bg-gray-900 border border-gray-700 text-white" />
              </div>
            ))}
            <Button onClick={saveInput} disabled={busy}>{busy ? 'Sending…' : 'Send to my manager'}</Button>
          </div>
        )}
      </Card>
    </div>
  );
}

export default MyReviewsPage;
